// ── 정산서 요약 헤더 (공유 컴포넌트) ──────────────────────────────────────────
// 지출 / 수입 / 정산받을돈 3항목을 +/- 부호와 함께 표시.
// 모든 회사 정산서(파인그룹·DOT·LNJ)와 관리자 정산서 관리에서 공통 사용.
//
// Props:
//   totalMinus  — 지출 합계 (API에서는 절대값)
//   totalPlus   — 수입 합계 (+금액 합계)
//   recTotal    — 정산지급 합계 — 지출에 합산됨
//   netExpected — 정산받을돈 (= netTotal - recTotal)
//   style       — 'compact' (모바일 가로 2칸) | 'row' (관리자 가로 나열) | 'badge' (PcSumBadge 스타일)

import React from 'react';

// ── 숫자 포맷: 3자리 콤마 ────────────────────────────────────────────────────
function fmt(v) {
  const n = Number(String(v ?? '').replace(/,/g, ''));
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('ko-KR');
}

// ── 부호 문자열 반환 ─────────────────────────────────────────────────────────
function sign(value) {
  if (value > 0) return '+';
  if (value < 0) return '-';
  return '';
}

/**
 * 정산서 요약 헤더 — 지출 / 수입 / 정산받을돈
 */
export function SummaryHeader({ totalMinus = 0, totalPlus = 0, recTotal = 0, netExpected = 0, variant, style: legacyStyle }) {
  const view = variant || legacyStyle || 'row';
  const minusAmt = Math.abs(Number(totalMinus) || 0) + Math.abs(Number(recTotal) || 0); // 지출 합산 (항상 - 표시)
  const plusAmt  = Number(totalPlus) || 0; // 수입 (+금액 합계)
  const netAmt   = netExpected;           // 정산받을돈 (동적 부호)

  if (view === 'compact') {
    // ── 모바일 히어로 스타일 (DOT/LNJ 직원 정산서용) ───────────────────────
    // 기존 heroStats 레이아웃 유지: width 100%, 충분한 패딩, 구분선
    return (
      <div style={{ display: 'flex', alignItems: 'stretch', width: '100%', marginTop: 20 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '12px 8px' }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 600, letterSpacing: '0.06em' }}>지출</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#fca5a5', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            -{fmt(Math.abs(minusAmt))}
          </span>
        </div>
        <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '12px 8px' }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 600, letterSpacing: '0.06em' }}>수입</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#6ee7b7', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {sign(plusAmt)}{fmt(Math.abs(plusAmt))}
          </span>
        </div>
      </div>
    );
  }

  if (view === 'badge') {
    // ── PC 뱃지 스타일 (직원 파인그룹 정산서 PcSumBadge 대체용) ────────────
    const badgeSt = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '6px 14px' };
    const labelSt = { fontSize: 10, color: 'var(--text-muted)' };
    const valSt = (color, bold) => ({ fontSize: 14, fontWeight: bold ? 900 : 700, color });
    return (
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
        <div style={badgeSt}>
          <span style={labelSt}>지출 합계</span>
          <span style={valSt('#dc2626', false)}>-{fmt(Math.abs(minusAmt))}원</span>
        </div>
        <div style={badgeSt}>
          <span style={labelSt}>수입 합계</span>
          <span style={valSt('#059669', false)}>{sign(plusAmt)}{fmt(Math.abs(plusAmt))}원</span>
        </div>
        <div style={badgeSt}>
          <span style={labelSt}>정산받을돈</span>
          <span style={valSt(netAmt >= 0 ? '#059669' : '#dc2626', true)}>
            {sign(netAmt)}{fmt(Math.abs(netAmt))}원
          </span>
        </div>
      </div>
    );
  }

  // ── 'row-divider' 스타일 (관리자 PC — 구분선 포함) ────────────────────────
  if (view === 'row-divider') {
    const divider = { width: 1, background: 'var(--border)', margin: '0 14px 0 0', alignSelf: 'stretch' };
    const valBase = { fontSize: 13, fontWeight: 700 };
    return (
      <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap', alignItems: 'stretch', marginTop: 4 }}>
        <div style={{ paddingRight: 14, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.3 }}>지출</span>
          <span style={{ ...valBase, color: '#dc2626' }}>-{fmt(Math.abs(minusAmt))}원</span>
        </div>
        <div style={divider} />
        <div style={{ paddingRight: 14, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.3 }}>수입</span>
          <span style={{ ...valBase, color: '#059669' }}>{sign(plusAmt)}{fmt(Math.abs(plusAmt))}원</span>
        </div>
        <div style={divider} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.3 }}>정산받을돈</span>
          <span style={{ ...valBase, color: netAmt >= 0 ? '#059669' : '#dc2626' }}>
            {sign(netAmt)}{fmt(Math.abs(netAmt))}원
          </span>
        </div>
      </div>
    );
  }

  // ── 기본 'row' 스타일 (관리자 정산서 관리 모바일 헤더용) ─────────────────
  const valBase = { fontSize: 13, fontWeight: 700 };
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>지출</span>
        <span style={{ ...valBase, color: '#dc2626' }}>
          -{fmt(Math.abs(minusAmt))}원
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>수입</span>
        <span style={{ ...valBase, color: '#059669' }}>
          {sign(plusAmt)}{fmt(Math.abs(plusAmt))}원
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>정산받을돈</span>
        <span style={{ ...valBase, color: netAmt >= 0 ? '#059669' : '#dc2626' }}>
          {sign(netAmt)}{fmt(Math.abs(netAmt))}원
        </span>
      </div>
    </div>
  );
}
